/**
 * Layout v2 – zweizeilige Toolbar (Navigation oben, Bulk unten).
 * Zurück zu v1: PPMatchingToolbar.layout-v1.js + matching-toolbar.layout-v1.css
 */
import React from "react"
import { useTranslation } from "react-i18next"
import { PPDefaultSelector } from "../ui/PPDefaultSelector"
import PPMatchingFilterPanel from "./PPMatchingFilterPanel"

export default function PPMatchingToolbar({
    shopOptions,
    selectedShop,
    onShopChange,
    shopsLoading,
    displayPosition,
    totalCount,
    position,
    jumpInput,
    onJumpInputChange,
    onJump,
    onFirst,
    onPrev,
    onNext,
    onLast,
    onBulkApprove,
    filtersExpanded,
    isLoading,
    approvingMatchId,
    bulkApproving,
    bulkMinScore,
    onBulkMinScoreChange,
    showBulkActions,
    producerOptions,
    selectedProducer,
    onProducerChange,
    producersLoading,
    filterReady,
    minScore,
    onMinScoreChange,
    matchSourceOptions,
    selectedMatchSource,
    onMatchSourceChange,
    matchTypesLoading
}) {
    const [t] = useTranslation()

    const filterBtnClass = "btn btn-outline-primary btn-sm pp-matching-toolbar__filter-btn"

    return (
        <section className="pp-matching-toolbar mb-3">
            <div className="pp-matching-toolbar__primary">
                <div className="pp-matching-toolbar__field pp-matching-toolbar__field--shop">
                    <label className="pp-matching-toolbar__label" htmlFor="matching-shop">
                        {t("matching.shop")}
                    </label>
                    <PPDefaultSelector
                        id="matching-shop"
                        data={shopOptions}
                        value={selectedShop}
                        onChange={(opt) => onShopChange(opt?.value ?? null)}
                        isClearable={false}
                        isDisabled={shopsLoading || shopOptions.length === 0}
                        placeholder={shopOptions.length === 0 ? t("matching.no_shops_title") : undefined}
                        menuInPortal
                    />
                </div>

                <div className="pp-matching-toolbar__field pp-matching-toolbar__field--nav">
                    <span className="pp-matching-toolbar__label">
                        {t("matching.position_of", { current: displayPosition, total: totalCount })}
                    </span>
                    <div className="pp-matching-nav">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={onFirst}
                            disabled={position <= 0 || isLoading || totalCount <= 0}
                            aria-label={t("matching.first_page")}
                        >
                            <i className="fas fa-angle-double-left" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={onPrev}
                            disabled={position <= 0 || isLoading || totalCount <= 0}
                            aria-label={t("matching.prev_page")}
                        >
                            <i className="fas fa-chevron-left" />
                        </button>
                        <form className="pp-matching-nav-jump" onSubmit={onJump}>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="form-control form-control-sm pp-matching-nav-input text-center"
                                value={jumpInput}
                                onChange={onJumpInputChange}
                                disabled={totalCount <= 1 || isLoading}
                                aria-label={t("matching.jump_input")}
                            />
                            <button
                                type="submit"
                                className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                                disabled={isLoading || totalCount <= 1}
                                title={t("matching.jump_title")}
                            >
                                {t("matching.jump")}
                            </button>
                        </form>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={onNext}
                            disabled={position + 1 >= totalCount || isLoading || totalCount <= 0}
                            aria-label={t("matching.next_page")}
                        >
                            <i className="fas fa-chevron-right" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={onLast}
                            disabled={position + 1 >= totalCount || isLoading || totalCount <= 0}
                            aria-label={t("matching.last_page")}
                        >
                            <i className="fas fa-angle-double-right" />
                        </button>
                    </div>
                </div>

                <div className="pp-matching-toolbar__field pp-matching-toolbar__field--filters">
                    <button
                        type="button"
                        className={filterBtnClass}
                        data-bs-toggle="collapse"
                        data-bs-target="#collapseMatchingFilter"
                        aria-expanded={filtersExpanded}
                        aria-controls="collapseMatchingFilter"
                    >
                        <i className="fas fa-filter me-1" aria-hidden="true" />
                        {t("matching.more_filters")}
                        <i
                            className={
                                "fas ms-1 pp-matching-filter-toggle-icon " +
                                (filtersExpanded ? "fa-angle-up" : "fa-angle-down")
                            }
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>

            {showBulkActions && (
                <div className="pp-matching-toolbar__bulk">
                    <div className="pp-matching-toolbar__bulk-text">
                        <span className="pp-matching-toolbar__bulk-title">{t("matching.bulk_actions")}</span>
                        <span className="pp-matching-toolbar__bulk-hint">{t("matching.bulk_min_score_hint")}</span>
                    </div>
                    <div className="pp-matching-toolbar__bulk-controls">
                        <label className="visually-hidden" htmlFor="matching-bulk-min-score">
                            {t("matching.bulk_min_score")}
                        </label>
                        <div className="input-group input-group-sm pp-matching-toolbar__bulk-score">
                            <span className="input-group-text">{t("matching.bulk_min_score_short")}</span>
                            <input
                                id="matching-bulk-min-score"
                                type="number"
                                className="form-control"
                                min={0}
                                max={100}
                                value={bulkMinScore}
                                onChange={onBulkMinScoreChange}
                                disabled={isLoading || bulkApproving}
                            />
                            <span className="input-group-text">%</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-success btn-sm pp-matching-toolbar__bulk-btn"
                            onClick={onBulkApprove}
                            disabled={isLoading || !!approvingMatchId || bulkApproving}
                        >
                            <i className="fas fa-check me-1" aria-hidden="true" />
                            {t("matching.bulk_assign")}
                        </button>
                    </div>
                </div>
            )}

            <div className="collapse pp-matching-toolbar__filters-collapse" id="collapseMatchingFilter">
                <PPMatchingFilterPanel
                    producerOptions={producerOptions}
                    selectedProducer={selectedProducer}
                    onProducerChange={onProducerChange}
                    producersLoading={producersLoading}
                    filterReady={filterReady}
                    minScore={minScore}
                    onMinScoreChange={onMinScoreChange}
                    matchSourceOptions={matchSourceOptions}
                    selectedMatchSource={selectedMatchSource}
                    onMatchSourceChange={onMatchSourceChange}
                    matchTypesLoading={matchTypesLoading}
                />
            </div>
        </section>
    )
}
